import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorDetail } from '@/utils/apiError';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import {
  useGetLifecycleQuery,
  useUpdateLifecycleMutation,
} from '@/redux/internal-services/onboardingApi';

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  Active: ['Suspended', 'Deactivated'],
  Suspended: ['Active', 'Deactivated'],
  Deactivated: ['Active'],
};

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  Active: 'success',
  Suspended: 'warning',
  Deactivated: 'danger',
};

const TenantLifecyclePage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useGetLifecycleQuery();
  const [updateLifecycle, { isLoading: saving }] = useUpdateLifecycleMutation();

  const lifecycle = data?.data;
  const currentStatus: string = lifecycle?.lifecycle_status ?? 'Active';
  const history: Array<{ from: string; to: string; reason: string; timestamp: string }> =
    (lifecycle?.lifecycle_history as Array<{ from: string; to: string; reason: string; timestamp: string }>) ?? [];

  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const allowedNext = ALLOWED_TRANSITIONS[currentStatus] ?? [];

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newStatus) { setError('Select a target status.'); return; }
    if (!reason.trim()) { setError('Reason is required.'); return; }
    try {
      await updateLifecycle({ status: newStatus, reason: reason.trim() }).unwrap();
      setSuccess(t('common.success'));
      setNewStatus('');
      setReason('');
      refetch();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  if (isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }

  return (
    <PageShell maxWidth="max-w-3xl" header={<PageHeader title={t('settings.lifecycle.title')} />}>
      <div className="card-surface mb-8 p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm font-medium text-ui-text2">Current Status:</span>
          <Badge variant={STATUS_VARIANT[currentStatus] ?? 'neutral'} className="px-3 py-1 text-sm font-semibold">
            {currentStatus}
          </Badge>
        </div>

        {allowedNext.length > 0 ? (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ui-text">Transition To</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className={fieldClass()}
              >
                <option value="">Select status...</option>
                {allowedNext.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ui-text">Reason *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className={fieldClass({ shape: 'box' })}
                placeholder="Required — explain the reason for this transition"
              />
            </div>
            {error && <p className="text-sm text-danger-text">{error}</p>}
            {success && <p className="text-sm text-success-text">{success}</p>}
            <Button type="submit" disabled={saving}>
              {saving ? t('common.loading') : 'Apply Transition'}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-ui-text2">{t('admin.noTransitionsAvailable')}</p>
        )}
      </div>

      {history.length > 0 && (
        <div>
          <h2 className="mb-3 text-h3">History</h2>
          <div className="space-y-2">
            {[...history].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-3 rounded-control bg-ui-raised p-3 text-sm">
                <div className="flex-1">
                  <span className="font-medium">{entry.from}</span>
                  <span className="mx-2 text-ui-text3">→</span>
                  <span className="font-medium">{entry.to}</span>
                  {entry.reason && <p className="mt-1 text-ui-text2">{entry.reason}</p>}
                </div>
                <span className="whitespace-nowrap text-ui-text3">
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default TenantLifecyclePage;
